import { useState } from "react";
import { Dropdown, Col, Row, Space, Typography, Modal, Button, message } from "antd";
import { Guid } from "guid-typescript";
import { SearchQuery } from "../code/search-query/search-query";
import Loader from "../controls/Loader";
import { useSelector } from "../store/store";
import { authorizeSelectors } from "../store/authorize";
import { Kpi } from "../repository/data.default";
import KpisView from "../components/kpis/KpisView";
import { getKpisExport } from "../repository/data.repository";
import KpiFormDialog from "../components/kpis/KpisFormDialog";
import { getProblemMessage } from "../utils/http";

function KpisPage() {
  const [modal, contextHolder] = Modal.useModal();
  const [searchQuery, setSearchQuery] = useState<SearchQuery>({
    conditions: [],
  });
  const [kpiInputMode, setKpiInputMode] = useState<"add" | "update" | "delete">(
    "add",
  );
  const [kpiInputOpen, setKpiInputOpen] = useState(false);
  const [kpiInputReadOnly, setKpiInputReadOnly] = useState(false);
  const [kpiInputValues, setKpiInputValues] = useState<Kpi>({} as any);
  const [triggerView, setTriggerView] = useState(Guid.create().toString());
  const permissions = useSelector(authorizeSelectors.permissions);

  function onSearch(searchQuery: SearchQuery): void {
    setSearchQuery(searchQuery);
  }

  function onKpiInputOpen(
    mode: "add" | "update" | "delete" | "view",
    values?: Kpi,
  ): void {
    // "view" = a KPI of a published/retired questionnaire version: the dialog in update layout, read-only.
    setKpiInputReadOnly(mode === "view");
    setKpiInputMode(mode === "view" ? "update" : mode);
    setKpiInputOpen(true);
    setKpiInputValues(
      values
        ? {
            uid: values.uid,
            rowVersion: values.rowVersion,
            name: values.name,
            description: values.description,
            shortDescription: values.shortDescription,
            type: {
              display: values.type,
              value: values.type,
            },
            scoringMethod: {
              display: values.scoringMethod,
              value: values.scoringMethod,
            },
            questionnaireUid: values.questionnaireUid,
            questionnaireName: values.questionnaireName,
            questionnaireVersion: values.questionnaireVersion,
            questionnaireStatus: values.questionnaireStatus,
          }
        : ({} as any),
    );
  }

  function onKpiInputFinish(): void {
    setKpiInputOpen(false);
    setKpiInputValues({} as any);
    setTriggerView(Guid.create().toString());
  }

  function onKpiInputClose(): void {
    setKpiInputOpen(false);
    setKpiInputValues({} as any);
  }

  function onDownloadExport(): void {
    const loading = modal.info({
      content: (
        <Typography.Text className="m-1 ml-2 inline-block">
          Downloading Kpis export file..
        </Typography.Text>
      ),
      footer: null,
      centered: true,
      icon: <Loader />,
    });
    getKpisExport({
      searchQuery,
      pageNumber: 0,
      pageSize: Number.MAX_SAFE_INTEGER,
    })
      .then(() => {
        loading.destroy();
      })
      .catch((err) => {
        loading.destroy();
        message.error(getProblemMessage(err) || "Failed to download export.");
      });
  }

  return (
    <Col className="relative flex flex-auto flex-col overflow-hidden overflow-y-auto">
      <Row className="sticky top-0 z-10 flex flex-row flex-wrap items-center p-4">
        <Typography.Title className="m-0 flex-auto" level={4}>
          KPIs
        </Typography.Title>
        <Space className="flex flex-row">
          <Dropdown.Button
            type="primary"
            disabled={!permissions.can("KPIs", "create")}

            className={`${!permissions.can("KPIs", "create") ? "disabled" : ""}`}
            menu={{
              items: [
                {
                  key: "downloadExport",
                  label: "Export to Excel",
                  disabled: !permissions.can("KPIs", "view"),
                  onClick: onDownloadExport,
                },
              ],
            }}
            onClick={() => onKpiInputOpen("add")}
          >
            Add Kpi
          </Dropdown.Button>
        </Space>
      </Row>
      <Col className="flex flex-auto flex-col overflow-hidden p-4 pb-4 pt-0">
        <KpisView
          trigger={triggerView}
          onAction={(key: string, record: any) =>
            onKpiInputOpen(key as any, record)
          }
          onSearch={onSearch}
        />
      </Col>
      <KpiFormDialog
        key={kpiInputMode}
        mode={kpiInputMode}
        open={kpiInputOpen}
        initialValues={kpiInputValues}
        readOnly={kpiInputReadOnly}
        onFinish={onKpiInputFinish}
        onCancel={onKpiInputClose}
      />
      {contextHolder}
    </Col>
  );
}

export default KpisPage;
